import React from "react";
import { Link } from "react-router-dom";
import { motion } from "@/lib/motion";
import { Star, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { staggerItem } from "@/components/Reveal";

export default function ProductCard({ product }) {
    const { addItem } = useCart();

    const quickAdd = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (product.sold_out) return;
        addItem({
            product_id: product.id,
            slug: product.slug,
            name: product.name,
            image: product.images?.[0],
            size_key: "cup12",
            size_label: "12oz Cup",
            unit_price: product.price,
            quantity: 1,
        });
        toast.success(`${product.name} added to cart`, { description: "12oz Cup · $" + product.price.toFixed(2) });
    };

    return (
        <motion.div variants={staggerItem} initial="hidden" animate="show">
            <Link
                to={`/product/${product.slug}`}
                data-testid={`product-card-${product.slug}`}
                className="group relative block rounded-3xl border border-caramel/10 bg-white p-4 pt-16 shadow-warm transition-all duration-300 hover:-translate-y-2 hover:shadow-warm-lg"
            >
                {/* Image breaks out of the top */}
                <div className="absolute -top-10 left-1/2 h-40 w-40 -translate-x-1/2 overflow-hidden rounded-full border-4 border-cream-50 bg-cream-200 shadow-warm-lg">
                    <img
                        src={product.images?.[0]}
                        alt={product.name}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                </div>

                {product.badge && (
                    <span className="absolute right-4 top-4 rounded-full bg-banana px-3 py-1 text-xs font-semibold text-roast shadow-pill">
                        {product.badge}
                    </span>
                )}
                {product.sold_out && (
                    <span className="absolute left-4 top-4 rounded-full bg-roast px-3 py-1 text-xs font-semibold text-cream-50">
                        Sold Out
                    </span>
                )}

                <div className="mt-6 text-center">
                    <div className="flex items-center justify-center gap-1 text-gold">
                        <Star size={14} className="fill-gold" />
                        <span className="text-sm font-medium text-roast">{product.rating}</span>
                        <span className="text-xs text-mocha">({product.review_count})</span>
                    </div>
                    <h3 className="mt-2 font-serif text-xl text-roast">{product.name}</h3>
                    <p className="mt-1 line-clamp-2 min-h-[40px] text-sm text-mocha">{product.short_desc}</p>
                    <div className="mt-4 flex items-center justify-between">
                        <span className="font-serif text-2xl text-caramel">${product.price.toFixed(2)}<span className="ml-1 align-middle text-xs font-sans font-medium text-mocha">/ 12oz</span></span>
                        <button
                            onClick={quickAdd}
                            disabled={product.sold_out}
                            data-testid={`quick-add-${product.slug}`}
                            className="flex items-center gap-1.5 rounded-full bg-roast px-4 py-2.5 text-sm font-medium text-cream-50 transition-all hover:bg-caramel active:scale-95 disabled:opacity-40"
                        >
                            <Plus size={16} /> Quick Add
                        </button>
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
